'use client';

import dayjs from 'dayjs';
import { useState } from 'react';

import { usePatchJobDeliverableMutation } from '@/features/jobs/api/jobsApi';
import { DatePicker, Popover } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './JobTasksComponents.module.scss';

import type { JobDeliverable } from '@/features/jobs/types';
import type { Dayjs } from 'dayjs';

type DeliverableDueDateControlProps = {
  jobId?: string;
  deliverableId: string;
  value: string | null | undefined;
  readOnly: boolean;
  onPatched?: (patch: Partial<JobDeliverable>) => void;
};

export function DeliverableDueDateControl({
  jobId,
  deliverableId,
  value,
  readOnly,
  onPatched,
}: DeliverableDueDateControlProps) {
  const [patchDeliverable, { isLoading }] = usePatchJobDeliverableMutation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const dueDate = value ? dayjs(value) : null;
  const dueDateLabel =
    dueDate && dueDate.isValid() ? dueDate.format('MMM D') : 'No due date';
  const isBusy = readOnly || isLoading;

  const handleDueDateChange = async (nextValue: Dayjs | null) => {
    const nextDueDate = nextValue?.isValid()
      ? nextValue.startOf('day').format('YYYY-MM-DD')
      : null;
    if ((value ?? null) === nextDueDate) {
      setAnchorEl(null);
      return;
    }

    try {
      const response = await patchDeliverable({
        ...(jobId ? { jobId } : {}),
        deliverableId,
        payload: { due_date: nextDueDate },
      }).unwrap();
      onPatched?.(response.data ?? { due_date: nextDueDate });
      setAnchorEl(null);
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <span className={styles.metricCard}>
      <button
        type="button"
        aria-label={`Update deliverable due date: ${dueDateLabel}`}
        className={styles.inlineMetaButton}
        disabled={isBusy}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {dueDateLabel}
      </button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <div className={styles.inlineMetaPicker}>
          <DatePicker
            name={`deliverable-due-date-${deliverableId}`}
            label="Due"
            value={dueDate}
            size="small"
            onChange={(dateValue: Dayjs | null) => {
              void handleDueDateChange(dateValue);
            }}
          />
        </div>
      </Popover>
    </span>
  );
}
